import { Box, Container, Typography } from '@mui/material';
import { JSX } from 'react';

const PrivacyPolicy = (): JSX.Element => {
  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
          Privacy Policy
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 6, textAlign: 'center' }}>
          Last updated: 11/13/2025
        </Typography>

        <Box sx={{ '& > *': { mb: 4 } }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
              When you use Group My Channels, we collect and process the following types of information:
            </Typography>

            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
              YouTube Data
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Your YouTube subscription list (channel IDs, names, thumbnails)</li>
              <li>Channel metadata required for organization (categories, descriptions)</li>
              <li>We do NOT access your YouTube watch history, personal videos, or private content</li>
            </Box>

            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
              Account Information
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Google account ID and email address (for authentication)</li>
              <li>Basic profile information (name, profile picture) if provided by Google</li>
              <li>Custom groups and channel organization preferences you create</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
              We use your information exclusively to provide and improve our service:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Organize your YouTube subscriptions into custom groups</li>
              <li>Sync and display your grouped channels</li>
              <li>Store your group preferences and settings</li>
              <li>Provide customer support when requested</li>
              <li>Improve our service based on usage patterns (anonymized data only)</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              3. Data Storage and Security
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
              We take data security seriously:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>All data is encrypted in transit and at rest</li>
              <li>We use industry-standard security measures to protect your information</li>
              <li>Data is stored on secure servers with access controls</li>
              <li>We regularly review and update our security practices</li>
              <li>We retain data only as long as necessary to provide our service</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              4. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
              We do not sell, trade, or rent your personal information to third parties. We may share information only
              in these limited circumstances:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or protect our legal rights</li>
              <li>
                With service providers who assist in operating our service (under strict confidentiality agreements)
              </li>
              <li>In the event of a business merger or acquisition (with advance notice)</li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              5. Your Rights and Controls
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
              You have several rights regarding your data:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Access:</strong> Request a copy of your data we have stored
              </li>
              <li>
                <strong>Correction:</strong> Update incorrect or incomplete information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your account and associated data
              </li>
              <li>
                <strong>Portability:</strong> Export your group configurations and settings
              </li>
              <li>
                <strong>Revocation:</strong> Revoke access permissions through your Google account settings
              </li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              To exercise these rights, contact us at support@groupmychannels.com
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              6. Cookies and Tracking
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              We use essential cookies to maintain your session and preferences. We do not use advertising cookies or
              third-party tracking scripts. You can control cookie settings in your browser, though disabling cookies
              may affect service functionality.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              7. Third-Party Services
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Our service integrates with:
            </Typography>
            <Box component="ul" sx={{ mt: 2, pl: 3 }}>
              <li>
                <strong>Google/YouTube API:</strong> Subject to Google's Privacy Policy
              </li>
              <li>
                <strong>Authentication providers:</strong> May collect data according to their privacy policies
              </li>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
              We recommend reviewing these third-party privacy policies.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              Our service is not intended for children under 13. We do not knowingly collect personal information from
              children under 13. If you believe we have collected such information, please contact us immediately.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              9. Changes to This Policy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              We may update this privacy policy from time to time. We will notify users of significant changes via email
              or through our service. Continued use after changes constitutes acceptance of the new policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              10. Contact Us
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <Box component="ul" sx={{ mt: 2, pl: 3 }}>
              <li>Email: support@groupmychannels.com</li>
              <li>General inquiries: support@groupmychannels.com</li>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
